// components/AboutSection.jsx
import React, { useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import SurfImage from "../pngs/Skimming.JPG";
import { primaryButtonBase } from "../styles/buttonStyles";
import ContactModal from "./ContactModal";
import { SectionHeadline } from "./Typography";
import { typography, accentColorPairs } from "../styles/designSystem";

const Section = styled.section`
  padding: 4rem 1rem;
  max-width: 1400px;
  margin: 0 auto;
  text-align: center;
`;

const Heading = styled(SectionHeadline)`
  text-align: center;
  margin-bottom: 3rem;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3rem;
  justify-content: center;
  align-items: flex-start;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 2rem;
    align-items: center;
  }
`;

const ImageWrapper = styled.div`
  flex: 1;
  max-width: 500px;
`;

const ImageFrame = styled.div`
  position: relative;
  border: 1px solid ${({ theme }) => theme.borderMedium};
  box-shadow: 0 0 24px ${({ theme }) => theme.glowSoft};

  /* Same scanline treatment the product previews get, so photographs sit inside
     the terminal illusion rather than on top of it. */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: repeating-linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.35) 0px,
      rgba(0, 0, 0, 0.35) 1px,
      transparent 1px,
      transparent 3px
    );
  }
`;

const Image = styled.img`
  display: block;
  width: 100%;
  object-fit: cover;
`;

const Caption = styled.div`
  font-family: ${typography.fontFamily.display};
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: ${({ theme }) => theme.subtleText};
  text-align: left;
  margin-top: 0.6rem;
`;

const Text = styled.div`
    flex: 1;
    max-width: 500px;
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
    text-align: left;

    p {
        margin: 0 0 1rem 0;
        text-align: left;

        @media (max-width: 1024px) {
            margin: 1rem 0 1rem 0;
        }

        &:last-of-type {
            margin-bottom: 0;
        }
    }
`;

const GetInTouchButton = styled.button`
    ${primaryButtonBase}
    padding: 0.7rem 1.3rem;
`;


const AboutSection = () => {
    // Phosphor hues only — see the note in ContactButton.
    const getRandomColor = () =>
        accentColorPairs[Math.floor(Math.random() * accentColorPairs.length)].bg;
    const [buttonColor] = useState(getRandomColor);
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <>
            <Section id='About'>
                <Heading>Personnel File</Heading>
                <ContentWrapper>
                    <ImageWrapper>
                        <ImageFrame>
                            <Image src={SurfImage} alt="Nicholas Lovett skimboarding" />
                        </ImageFrame>
                        <Caption>&gt; Personnel file // off-duty log</Caption>
                    </ImageWrapper>
                    <Text>
                        <p>I've spent the last decade building software as a product engineer — inside enterprise teams at AT&amp;T, Spectrum, Verizon, and Caterpillar, and as a solo founder shipping my own products. The through-line is ownership: I take work from problem statement to deployed software rather than handing it across a wall halfway through.</p>
                        <p>In practice that means React and React Native on the front end, Node and Next.js services behind them, and the component libraries, token architecture, and API design that keep the whole thing maintainable. I care about production code that holds up — not prototypes that demo well and collapse under next quarter's roadmap.</p>
                        <p>Outside of work I recharge outdoors — hiking, mountain biking, surfing — and by cooking. It keeps the problem-solving muscle loose.</p>

                        <GetInTouchButton
                            onClick={() => setIsModalOpen(true)}
                            color={buttonColor}
                        >
                            <FontAwesomeIcon icon={faEnvelope} />
                            Open Channel
                        </GetInTouchButton>
                    </Text>
                </ContentWrapper>
            </Section>
            <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </>
    );
};

export default AboutSection;
